/**
 * UltraAdminOrganizerCategoriesPage
 * ──────────────────────────────────
 * Ultra Admin, organizer ürünlerinin kategori ve alt-kategorilerini merkezi
 * olarak yönetir. Ürün formu ve modüler paket editörü bu listeyi kullanır.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tag, Plus, Trash2, Edit2, X, RotateCcw, Check
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import BilingualField from '../components/BilingualField';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/apiClient';
import { toast } from 'sonner';

export default function UltraAdminOrganizerCategoriesPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | cat obj
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token');
    if (!t) navigate('/giris?tab=ultra');
  }, [navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/organizer-categories');
      setCategories(r.data || []);
    } catch {
      toast.error(tr ? 'Kategoriler yüklenemedi.' : 'Failed to load categories.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiClient.delete(`/ultra-admin/organizer-categories/${confirmDelete.id}`);
      toast.success(tr ? 'Kategori silindi.' : 'Category deleted.');
      setConfirmDelete(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 overflow-auto">
        <UltraAdminTopbar
          title={tr ? 'Ürün Kategorileri' : 'Product Categories'}
          subtitle={tr ? 'Organizatör ürünlerinin kategori ve alt-kategori tanımları.' : 'Category & sub-category definitions for organizer products.'}
          icon={Tag}
          onRefresh={fetchAll}
          rightExtra={
            <button
              onClick={() => setEditing('new')}
              data-testid="create-category-btn"
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-xl text-sm shadow-sm"
            >
              <Plus className="h-4 w-4" /> {tr ? 'Yeni Kategori' : 'New Category'}
            </button>
          }
        />
        <div className="p-8">
          <PageIntro {...getPageIntro('organizer-categories', language)} />
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Tag className="h-14 w-14 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-3">{tr ? 'Henüz kategori yok.' : 'No categories yet.'}</p>
              <button
                onClick={() => setEditing('new')}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-xl text-sm"
              >
                <Plus className="h-4 w-4" /> {tr ? 'İlk Kategoriyi Ekle' : 'Add First Category'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map(c => (
                <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`category-card-${c.id}`}>
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">
                        {tr ? (c.name_tr || c.name_en) : (c.name_en || c.name_tr)}
                      </h3>
                      <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                        <span className={c.name_tr ? 'font-semibold text-gray-600' : ''}>TR {c.name_tr ? '✓' : '·'}</span>
                        <span>·</span>
                        <span className={c.name_en ? 'font-semibold text-gray-600' : ''}>EN {c.name_en ? '✓' : '·'}</span>
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(c)} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200" data-testid={`edit-category-${c.id}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => setConfirmDelete(c)} className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                      {tr ? `Alt Kategoriler (${(c.sub_categories || []).length})` : `Sub-Categories (${(c.sub_categories || []).length})`}
                    </p>
                    {(c.sub_categories || []).length === 0 ? (
                      <p className="text-xs text-gray-400 italic">{tr ? 'Yok' : 'None'}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(c.sub_categories || []).map(s => (
                          <span key={s.id} className="bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">
                            {tr ? (s.name_tr || s.name_en) : (s.name_en || s.name_tr)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <CategoryEditorModal
          tr={tr}
          cat={editing === 'new' ? null : editing}
          onClose={() => { setEditing(null); fetchAll(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title={tr ? 'Kategoriyi Sil' : 'Delete Category'}
        message={tr
          ? `"${confirmDelete?.name_tr || ''}" kategorisini silmek istediğinize emin misiniz?\n\nBu kategori ürünler tarafından kullanılıyorsa silme engellenir.`
          : `Delete "${confirmDelete?.name_en || ''}"?\n\nBlocked if any product uses this category.`
        }
        confirmLabel={tr ? 'Evet, Sil' : 'Yes, Delete'}
        cancelLabel={tr ? 'Vazgeç' : 'Cancel'}
        variant="danger"
        onConfirm={performDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function CategoryEditorModal({ tr, cat, onClose }) {
  const isEdit = !!cat;
  const [nameTr, setNameTr] = useState(cat?.name_tr || '');
  const [nameEn, setNameEn] = useState(cat?.name_en || '');
  const [subs, setSubs] = useState((cat?.sub_categories || []).map(s => ({ ...s })));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const addSub = () => setSubs(prev => [{ id: `new-${Date.now()}`, name_tr: '', name_en: '' }, ...prev]);
  const updateSub = (idx, patch) => setSubs(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const removeSub = (idx) => setSubs(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setError(null);
    if (!nameTr.trim() && !nameEn.trim()) {
      setError(tr ? 'Kategori adı TR ve EN için girilmelidir.' : 'Name required in both TR and EN.');
      return;
    }
    if (!nameTr.trim()) { setError(tr ? 'TR adı zorunludur.' : 'TR name required.'); return; }
    if (!nameEn.trim()) { setError(tr ? 'EN adı zorunludur.' : 'EN name required.'); return; }
    for (const s of subs) {
      if (!s.name_tr.trim() || !s.name_en.trim()) {
        setError(tr ? 'Tüm alt kategoriler TR ve EN adları ile doldurulmalı.' : 'All sub-categories need both TR and EN names.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name_tr:        nameTr.trim(),
        name_en:        nameEn.trim(),
        sub_categories: subs.map(s => ({
          id:      s.id?.startsWith('new-') ? undefined : s.id,
          name_tr: s.name_tr.trim(),
          name_en: s.name_en.trim(),
        })),
      };
      if (isEdit) {
        await apiClient.put(`/ultra-admin/organizer-categories/${cat.id}`, payload);
      } else {
        await apiClient.post('/ultra-admin/organizer-categories', payload);
      }
      toast.success(tr ? 'Kaydedildi.' : 'Saved.');
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="category-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-green-50">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Tag className="h-5 w-5 text-white" />
            </div>
            <h2 className="font-bold text-gray-900 text-lg">
              {isEdit ? (tr ? 'Kategoriyi Düzenle' : 'Edit Category') : (tr ? 'Yeni Kategori' : 'New Category')}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/50 text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <BilingualField
            label={tr ? 'Kategori Adı' : 'Category Name'} required
            valueTr={nameTr} valueEn={nameEn}
            onChangeTr={setNameTr} onChangeEn={setNameEn}
            placeholderTr="Ör: Teknoloji" placeholderEn="E.g. Technology"
            focusRingClass="focus:ring-emerald-200"
            testIdBase="cat-name"
          />
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">{tr ? 'Alt Kategoriler' : 'Sub-Categories'}</h3>
              <button type="button" onClick={addSub} className="flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-semibold text-xs px-3 py-1.5 rounded-lg">
                <Plus className="h-3.5 w-3.5" /> {tr ? 'Ekle' : 'Add'}
              </button>
            </div>
            {subs.length === 0 ? (
              <p className="text-xs text-gray-400 italic">{tr ? 'Henüz alt kategori yok.' : 'No sub-categories yet.'}</p>
            ) : (
              <div className="space-y-2">
                {subs.map((s, idx) => (
                  <div key={s.id} className="flex items-start gap-2 bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <input
                        type="text" value={s.name_tr}
                        onChange={e => updateSub(idx, { name_tr: e.target.value })}
                        placeholder={tr ? 'TR (Türkçe)' : 'TR (Turkish)'}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white"
                      />
                      <input
                        type="text" value={s.name_en}
                        onChange={e => updateSub(idx, { name_en: e.target.value })}
                        placeholder={tr ? 'EN (İngilizce)' : 'EN (English)'}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white"
                      />
                    </div>
                    <button type="button" onClick={() => removeSub(idx)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 mt-0.5">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 rounded-xl">
            {tr ? 'Kapat' : 'Close'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-2 rounded-xl text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {saving
              ? <><RotateCcw className="h-4 w-4 animate-spin" /> {tr ? 'Kaydediliyor...' : 'Saving...'}</>
              : <><Check className="h-4 w-4" /> {isEdit ? (tr ? 'Güncelle' : 'Update') : (tr ? 'Kaydet' : 'Save')}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
